import { LoaderCircle, Shield, Trash2, UserRoundMinus, UserRoundPlus, X } from 'lucide-react'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import {
  listBlockedMembers, MAX_MODERATION_REASON_LENGTH, moderationCopy, moderationRequest,
  performAdminModeration, removalAction,
  type BlockedMember, type ModerationAction, type ModerationCompletion, type ModerationTarget,
} from './adminModeration'

export type AdminModerationDialogProps = {
  open: boolean
  target: ModerationTarget | null
  currentAdminId?: string
  onClose: () => void
  onCompleted: (result: ModerationCompletion) => void
}

export default function AdminModerationDialog({ open, target, currentAdminId, onClose, onCompleted }: AdminModerationDialogProps) {
  const [action, setAction] = useState<ModerationAction>('block_member')
  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [listBusy, setListBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [members, setMembers] = useState<BlockedMember[]>([])
  const [selectedMember, setSelectedMember] = useState<BlockedMember | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [listLoaded, setListLoaded] = useState(false)
  const dialogRef = useRef<HTMLElement>(null)
  const busyRef = useRef(false)
  const listBusyRef = useRef(false)
  const generationRef = useRef(0)
  const listController = useRef<AbortController | null>(null)
  const mutationController = useRef<AbortController | null>(null)
  const closeRef = useRef(onClose)
  const previousFocus = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descriptionId = useId()
  const targetKey = target ? `${target.kind}:${target.id}` : 'member-manager'

  useEffect(() => { closeRef.current = onClose }, [onClose])

  async function loadMembers(after: string | null, generation: number, controller: AbortController) {
    if (listBusyRef.current) return
    listBusyRef.current = true
    setListBusy(true)
    setError('')
    try {
      const page = await listBlockedMembers({ after, signal: controller.signal })
      if (generationRef.current !== generation || controller.signal.aborted) return
      setMembers((current) => {
        const entries = after ? [...current, ...page.blocked_members] : page.blocked_members
        return [...new Map(entries.map((member) => [member.id, member])).values()]
      })
      setHasMore(page.has_more)
      setNextCursor(page.next_cursor)
      setListLoaded(true)
    } catch (requestError) {
      if (generationRef.current !== generation || controller.signal.aborted) return
      setError(requestError instanceof Error ? requestError.message : 'The blocked-member list could not be loaded.')
    } finally {
      if (generationRef.current === generation) {
        listBusyRef.current = false
        setListBusy(false)
      }
    }
  }

  useEffect(() => {
    const generation = ++generationRef.current
    listController.current?.abort()
    mutationController.current?.abort()
    busyRef.current = false
    listBusyRef.current = false
    setBusy(false)
    setListBusy(false)
    setReason('')
    setError('')
    setNotice('')
    setConfirming(false)
    setSelectedMember(null)
    setMembers([])
    setHasMore(false)
    setNextCursor(null)
    setListLoaded(false)
    setAction(target ? removalAction(target.kind) ?? 'block_member' : 'unblock_member')
    if (open && !target) {
      const controller = new AbortController()
      listController.current = controller
      void loadMembers(null, generation, controller)
    }
    return () => {
      ++generationRef.current
      listController.current?.abort()
      mutationController.current?.abort()
    }
    // A different entity resets confirmation; unrelated parent refreshes do not.
  }, [open, targetKey, currentAdminId])

  useEffect(() => {
    if (!open) return
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const priorOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const timer = window.setTimeout(() => dialogRef.current?.focus(), 20)
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        if (!busyRef.current) closeRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      ) ?? [])].filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null)
      if (!focusable.length) { event.preventDefault(); dialogRef.current?.focus(); return }
      const first = focusable[0], last = focusable[focusable.length - 1]
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault(); first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = priorOverflow
      previousFocus.current?.focus()
    }
  }, [open])

  if (!open) return null

  const isManager = target === null
  const selection = isManager ? selectedMember : null
  const actorName = selection?.display_name || target?.authorName || 'this member'
  const targetId = isManager ? selection?.id : action === 'block_member'
    ? target.kind === 'member' ? target.id : target.authorId
    : target.id
  const copy = moderationCopy(action, actorName)
  const removeAction = target ? removalAction(target.kind) : null
  const isProtectedSelf = action === 'block_member' && targetId === currentAdminId
  const canBlockAuthor = Boolean(target?.authorId && target.authorId !== currentAdminId)

  function changeAction(next: ModerationAction) {
    if (busyRef.current) return
    setAction(next)
    setConfirming(false)
    setError('')
    setNotice('')
  }

  function selectUnblock(member: BlockedMember) {
    if (busyRef.current || listBusyRef.current) return
    setSelectedMember(member)
    setAction('unblock_member')
    setConfirming(true)
    setReason('')
    setError('')
    setNotice('')
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busyRef.current || listBusyRef.current || !targetId || !confirming || isProtectedSelf) return
    let request
    try { request = moderationRequest(action, targetId, reason) }
    catch (requestError) { setError(requestError instanceof Error ? requestError.message : 'The request could not be prepared.'); return }
    const generation = generationRef.current
    const controller = new AbortController()
    mutationController.current = controller
    busyRef.current = true
    setBusy(true)
    setError('')
    let result: ModerationCompletion | null = null
    try {
      result = await performAdminModeration(request, controller.signal)
      if (generationRef.current !== generation || controller.signal.aborted) return
      if (isManager) {
        setMembers((current) => current.filter((member) => member.id !== result?.target_id))
        setSelectedMember(null)
        setConfirming(false)
        setReason('')
        setNotice(`${actorName} is unblocked. They can sign in again.`)
      }
    } catch (requestError) {
      if (generationRef.current !== generation || controller.signal.aborted) return
      setError(requestError instanceof Error ? requestError.message : 'The administrative request could not be completed.')
    } finally {
      if (generationRef.current === generation) { busyRef.current = false; setBusy(false) }
    }
    if (result && generationRef.current === generation && !controller.signal.aborted) {
      onCompleted(result)
      if (!isManager) onClose()
    }
  }

  return (
    <div className="admin-moderation-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busyRef.current) onClose()
    }}>
      <section ref={dialogRef} className="admin-moderation-dialog" role="dialog" aria-modal="true"
        aria-labelledby={titleId} aria-describedby={descriptionId} tabIndex={-1} aria-busy={busy || listBusy}>
        <button type="button" className="admin-moderation-close" aria-label="Close administration" disabled={busy} onClick={onClose}><X size={20} /></button>
        <header className="admin-moderation-header">
          <Shield size={26} aria-hidden="true" />
          <div><span className="eyebrow">ADMINISTRATION</span><h2 id={titleId}>{isManager ? 'Blocked members' : target.kind === 'member' ? 'Block member' : 'Moderate content'}</h2></div>
        </header>
        <p id={descriptionId}>Only site administrators can use these controls. Actions are recorded; administrator accounts and your own account are protected.</p>
        {notice && <p className="admin-moderation-notice" role="status">{notice}</p>}
        {isManager && !selection && <div className="admin-moderation-members">
          {listBusy && <p role="status"><LoaderCircle size={16} className="spin" aria-hidden="true" /> Loading blocked members…</p>}
          {listLoaded && members.length === 0 && !listBusy && <p>No blocked members.</p>}
          {members.map((member) => <div key={member.id} className="admin-moderation-member">
            <div><strong>{member.display_name}</strong><span>@{member.handle}</span></div>
            <button type="button" disabled={busy || listBusy} onClick={() => selectUnblock(member)}><UserRoundPlus size={16} /> Unblock</button>
          </div>)}
          {hasMore && <button type="button" className="admin-moderation-load-more" disabled={busy || listBusy} onClick={() => {
            const controller = listController.current
            if (controller && nextCursor) void loadMembers(nextCursor, generationRef.current, controller)
          }}>Load more blocked members</button>}
          {!listBusy && error && <button type="button" onClick={() => {
            const controller = listController.current
            if (controller) void loadMembers(null, generationRef.current, controller)
          }}>Retry blocked-member list</button>}
        </div>}
        {!isManager && <div className="admin-moderation-target">
          <strong>{target.authorName || 'Selected member'}</strong>
          {target.body && <blockquote>{target.body.slice(0, 1000)}{target.body.length > 1000 ? '…' : ''}</blockquote>}
          {removeAction && <div className="admin-moderation-choices">
            <button type="button" aria-pressed={action === removeAction} disabled={busy} className={action === removeAction ? 'is-selected' : ''} onClick={() => changeAction(removeAction)}><Trash2 size={16} /> Remove {target.kind === 'post' ? 'post' : target.kind === 'comment' ? 'reply' : 'message'}</button>
            {canBlockAuthor && <button type="button" aria-pressed={action === 'block_member'} disabled={busy} className={action === 'block_member' ? 'is-selected' : ''} onClick={() => changeAction('block_member')}><UserRoundMinus size={16} /> Block author</button>}
          </div>}
        </div>}
        {(!isManager || selection) && <form className="admin-moderation-confirmation" onSubmit={(event) => { void submit(event) }}>
          <h3>{copy.title}</h3><p>{copy.description}</p>
          {isProtectedSelf && <p className="admin-moderation-notice" role="status">Your administrator account is protected and cannot be blocked.</p>}
          <label className="admin-moderation-field">Reason <span>(optional; administrative record)</span>
            <textarea value={reason} maxLength={MAX_MODERATION_REASON_LENGTH} disabled={busy} rows={3} onChange={(event) => setReason(event.target.value)} />
            <small>{reason.length}/{MAX_MODERATION_REASON_LENGTH}</small>
          </label>
          {confirming && <p className="admin-moderation-confirm-warning">Confirm only if this is the intended member or content.</p>}
          <div className="admin-moderation-actions">
            <button type="button" disabled={busy} onClick={() => {
              if (isManager) { setSelectedMember(null); setConfirming(false); setReason(''); setError('') }
              else if (confirming) setConfirming(false)
              else onClose()
            }}>{isManager || confirming ? 'Back' : 'Cancel'}</button>
            {/* Review must not become a submit button during the same click. */}
            {confirming ? <button key="moderation-submit" type="submit" className="admin-moderation-confirm" disabled={busy || !targetId || isProtectedSelf}>
              {busy && <LoaderCircle size={16} className="spin" aria-hidden="true" />}{busy ? 'Working…' : copy.confirmLabel}
            </button> : <button key="moderation-review" type="button" className="admin-moderation-confirm" disabled={busy || !targetId || isProtectedSelf} onClick={(event) => { event.preventDefault(); setConfirming(true); setError('') }}>{copy.reviewLabel}</button>}
          </div>
        </form>}
        {error && <p className="admin-moderation-error" role="alert">{error}</p>}
      </section>
    </div>
  )
}
